// components/StandingsSection.jsx
import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Avatar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useNavigate } from "react-router-dom";
import { getTeamImage, useTeamImageFallback } from "../utils/teamImages";

function getStandingsPoints(driver) {
  return driver?.current_points ?? driver?.points ?? 0;
}

function getPointsBreakdown(driver) {
  const points = getStandingsPoints(driver);
  const gross = driver?.gross_points ?? points;
  const dropped = driver?.dropped_points ?? Math.max(0, gross - points);
  return `Championship points: ${points}. Points earned: ${gross}. Dropped points: ${dropped}. Drop rounds remove a driver's worst results from the championship total.`;
}

/**
 * Standings section: driver & team tables.
 * Mobile-first:
 * - Drivers table becomes a stacked "list" on xs, full table on sm+
 * Props: { intro, drop, driverRows = [], teamRows = [] }
 */
export default function StandingsSection({
  intro,
  drop,
  driverRows = [],
  teamRows = [],
  drivers = [],
}) {
  const navigate = useNavigate();

  const navigateToTeam = (event, teamName) => {
    event.stopPropagation();
    if (!teamName) return;
    navigate(`/team/${encodeURIComponent(teamName)}`);
  };

  const handleTeamKeyDown = (event, teamName) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      navigateToTeam(event, teamName);
    }
  };

  const driverById = useMemo(() => {
    const m = new Map();
    (drivers || []).forEach((d) => m.set(d.id, d));
    return m;
  }, [drivers]);

  const rows = useMemo(() => {
    return [...(driverRows || [])].sort((a, b) => getStandingsPoints(b) - getStandingsPoints(a));
  }, [driverRows]);
  const teamStandings = useMemo(() => {
    // Driver rows are the source of truth for computed championship points on
    // every simulator tab. Fall back to configured teams only when there are
    // no driver standings yet.
    if (rows.length > 0) {
      const totals = new Map();
      rows.forEach((driver) => {
        const profile = driverById.get(driver.id) || {};
        const name = profile.team || driver.team || "Private";
        const team = totals.get(name) || { name, points: 0, drivers: [] };
        team.points += getStandingsPoints(driver);
        team.drivers.push(driver.id);
        totals.set(name, team);
      });
      return Array.from(totals.values()).sort(
        (a, b) => b.points - a.points || a.name.localeCompare(b.name)
      );
    }

    return [...(teamRows || [])]
      .filter((team) => team?.name)
      .sort((a, b) => (b.points ?? 0) - (a.points ?? 0));
  }, [driverById, rows, teamRows]);

  return (
    <>
      <Typography sx={{ mb: 2 }}>
        {intro ?? "Driver & Team points, drop rounds, class splits, and stats."}
      </Typography>

      {/* Drivers Championship */}
      {rows.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Drivers Championship
          </Typography>

          {/* Mobile: stacked list (no horizontal scroll table) */}
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
              {rows.map((d, i) => {
                const profile = driverById.get(d.id) || {};
                return (
                  <Box
                    key={`driver-xs-${d.id}-${i}`}
                    onClick={() => navigate(`/driver/${d.id}`)}
                    sx={{
                      cursor: "pointer",
                      p: 1.5,
                      "&:active": { opacity: 0.85 },
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 34,
                          textAlign: "center",
                          fontWeight: 900,
                          fontSize: 14,
                          opacity: 0.9,
                        }}
                      >
                        {i + 1}
                      </Box>

                      <Avatar
                        src={profile.image}
                        alt={`${profile.name ?? d.name} profile`}
                        sx={{ width: 40, height: 40 }}
                      />

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 900 }} noWrap>
                          {profile.name ?? d.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {profile.team ?? d.team ?? ""} • {profile.car ?? d.car ?? ""}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: "wrap" }}>
                          <Tooltip title={getPointsBreakdown(d)} arrow>
                            <Chip
                              size="small"
                              label={`${getStandingsPoints(d)} pts`}
                              color="primary"
                            />
                          </Tooltip>
                          <Chip size="small" label={`W ${d.current_wins ?? 0}`} />
                          <Chip size="small" label={`P ${d.current_podiums ?? 0}`} />
                        </Stack>
                      </Box>
                    </Stack>

                    {i !== rows.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                  </Box>
                );
              })}
            </Paper>
          </Box>

          {/* Tablet/Desktop: full table */}
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Box sx={{ overflowX: "auto", width: "100%" }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Team</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="flex-end">
                        <span>Points</span>
                        <Tooltip title="Championship points after any drop rounds. Hover or tap a driver's points for their full breakdown." arrow>
                          <IconButton size="small" aria-label="About championship points">
                            <HelpOutlineIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">Wins</TableCell>
                    <TableCell align="right">Podiums</TableCell>
                    <TableCell>Car</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((d, i) => {
                    const profile = driverById.get(d.id) || {};
                    const teamName = profile.team ?? d.team ?? "";
                    return (
                      <TableRow
                        key={`driver-sm-${d.id}-${i}`}
                        onClick={() => navigate(`/driver/${d.id}`)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>
                          <Box
                            display={"flex"}>
                            <Avatar
                              src={profile.image}
                              alt={`${profile.name ?? d.name} profile`}
                              sx={{ width: 40, height: 40 }}
                            />
                            <Typography variant="body2" color="text.primary" sx={{margin: "auto"}} noWrap>{profile.name ?? d.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell
                          role={teamName ? "link" : undefined}
                          tabIndex={teamName ? 0 : undefined}
                          onClick={(event) => navigateToTeam(event, teamName)}
                          onKeyDown={(event) => handleTeamKeyDown(event, teamName)}
                          sx={teamName ? { cursor: "pointer", "&:hover": { textDecoration: "underline" } } : undefined}
                        >
                          <Box display={"flex"} alignItems={"center"} gap={1}>
                            <img src={getTeamImage(teamName)} onError={useTeamImageFallback} alt="" width={50}/>
                            <Typography sx={{display: {sm: "none", md: "block"}}}>
                              {teamName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={getPointsBreakdown(d)} arrow>
                            <Box component="span" sx={{ cursor: "help", borderBottom: "1px dotted", borderColor: "text.secondary" }}>
                              {getStandingsPoints(d)}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">{d.current_wins ?? 0}</TableCell>
                        <TableCell align="right">{d.current_podiums ?? 0}</TableCell>
                        <TableCell>{profile.car ?? d.car ?? ""}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>
      )}

      {teamStandings.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Teams Championship
          </Typography>
          <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell align="right">Points</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamStandings.map((team, index) => (
                  <TableRow key={`${team.name}-${index}`}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell
                      role="link"
                      tabIndex={0}
                      onClick={(event) => navigateToTeam(event, team.name)}
                      onKeyDown={(event) => handleTeamKeyDown(event, team.name)}
                      sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <img src={getTeamImage(team.name)} onError={useTeamImageFallback} alt="" width={50} />
                        <Typography>{team.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{team.points ?? 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}
    </>
  );
}
